import React, { useEffect, useMemo, useState } from "react";
import ImportColumnMapper from "./ImportColumnMapper";
import ImportPreview from "./ImportPreview";
import ImportSummary from "./ImportSummary";
import ImportFileUpload from "./ImportFileUpload";
import { AccountType } from "../../../models/AccountType";
import type { ImportSummary as ImportSummaryType } from "../../../services/UserService";
import { useUsers } from "../../../contexts/UsersContext";
import { useClassroom } from "../../../contexts/ClassroomContext";
import { listClassrooms } from "../../../services/ClassroomService";

export interface ColumnMapping {
    [columnIndex: number]: string[]; // column index -> array of field keys (split by space, in order)
}

export interface ImportWizardProps {
    show?: boolean;
    userType: string;
    onHide?: () => void;
    onClose?: () => void;
    classroomName?: string;
    classroomId?: string;
    termId?: string;
    onSuccess?: () => void;
}

type WizardStep = "upload" | "map" | "preview" | "summary";

function ImportWizard({ show = true, userType, onHide = () => {}, onClose, classroomName, classroomId, termId, onSuccess }: ImportWizardProps) {
    const { triggerRefresh } = useUsers();
    const { setPendingStudentEnrollment } = useClassroom();
    const [step, setStep] = useState<WizardStep>("upload");
    const [rawData, setRawData] = useState<string[][]>([]);
    const [headers, setHeaders] = useState<string[]>([]);
    const [mapping, setMapping] = useState<ColumnMapping>({});
    const [manualFields, setManualFields] = useState<string[]>([]);
    const [summary, setSummary] = useState<ImportSummaryType | null>(null);
    const [validClassroomNames, setValidClassroomNames] = useState<string[]>([]);

    const fields = useMemo(() => {
        if (userType === AccountType.TEACHER) {
            return [
                { key: "first_name", label: "First Name", required: true },
                { key: "last_name", label: "Last Name", required: true },
                { key: "other_names", label: "Other Names", required: false },
                { key: "email", label: "Email", required: true },
                { key: "phone_no", label: "Phone", required: false },
            ];
        }
        if (userType === AccountType.GUARDIAN) {
            return [
                { key: "first_name", label: "First Name", required: false },
                { key: "last_name", label: "Last Name", required: false },
                { key: "other_names", label: "Other Names", required: false },
                { key: "email", label: "Email", required: false },
                { key: "phone_no", label: "Phone", required: false },
                { key: "student_email", label: "Student Email", required: false },
                { key: "student_relationship_type", label: "Student Relationship Type", required: false },
            ];
        }
        // student
        return [
            { key: "first_name", label: "First Name", required: true },
            { key: "last_name", label: "Last Name", required: true },
            { key: "other_names", label: "Other Names", required: false },
            { key: "email", label: "Email", required: false },
            { key: "phone_no", label: "Phone", required: false },
            { key: "gender", label: "Gender", required: false },
            { key: "dob", label: "Date of Birth", required: false },
            { key: "guardian_first_name", label: "Guardian First Name", required: false },
            { key: "guardian_last_name", label: "Guardian Last Name", required: false },
            { key: "guardian_email", label: "Guardian Email", required: false },
            { key: "guardian_phone", label: "Guardian Phone", required: false },
            { key: "guardian_relationship_type", label: "Guardian Relationship Type", required: false },
            { key: "class_name", label: "Class", required: false },
        ];
    }, [userType]);

    const previewData = useMemo(() => rawData.slice(0, 10), [rawData]);

    useEffect(() => {
        if (show) return;
        setStep("upload");
        setRawData([]);
        setHeaders([]);
        setMapping({});
        setManualFields([]);
        setSummary(null);
        setValidClassroomNames([]);
    }, [show]);

    // Fetch valid classroom names when entering preview step for students
    useEffect(() => {
        if (step === "preview" && userType === AccountType.STUDENT && !classroomName) {
            listClassrooms(0, 500)
                .then(res => setValidClassroomNames(res.items.map(c => c.name.toLowerCase())))
                .catch(() => setValidClassroomNames([]));
        }
    }, [step, userType, classroomName]);

    const handleFileUploaded = (data: string[][], detectedHeaders: string[]) => {
        setRawData(data);
        setHeaders(detectedHeaders);
        setStep("map");
    };

    const handleMappingComplete = (newMapping: ColumnMapping, manual: string[]) => {
        setMapping(newMapping);
        setManualFields(manual);
        setStep("preview");
    };

    const handleImportComplete = (result: ImportSummaryType) => {
        const enrolledFromGradebook = (
            userType === AccountType.STUDENT
            && classroomId
            && termId
            && result.created > 0
        );
        if (enrolledFromGradebook) {
            setPendingStudentEnrollment({ classroomId, termId });
        }
        setSummary(result);
        setStep("summary");
        if (!enrolledFromGradebook) {
            triggerRefresh();
        }
        onSuccess?.();
    };

    if (!show) return null;

    return (
        <>
            {step === "upload" && (
                <ImportFileUpload
                    userType={userType}
                    onBack={onHide}
                    onClose={onClose}
                    onFileUploaded={handleFileUploaded}
                />
            )}
            {step === "map" && (
                <ImportColumnMapper
                    userType={userType}
                    headers={headers}
                    previewData={previewData}
                    fields={fields}
                    onBack={() => setStep("upload")}
                    onDismiss={onHide}
                    onClose={onClose}
                    onMappingComplete={handleMappingComplete}
                    lockedClassroomName={classroomName}
                />
            )}
            {step === "preview" && (
                <ImportPreview
                    userType={userType}
                    rawData={rawData}
                    mapping={mapping}
                    manualFields={manualFields}
                    fields={fields}
                    onBack={() => setStep("map")}
                    onDismiss={onHide}
                    onClose={onClose}
                    onImportComplete={handleImportComplete}
                    lockedClassroomName={classroomName}
                    validClassroomNames={validClassroomNames}
                />
            )}
            {step === "summary" && summary && (
                <ImportSummary
                    summary={summary}
                    onDone={onHide}
                    onClose={onClose}
                />
            )}
        </>
    );
}

export default ImportWizard;
